import { EditorView } from "@codemirror/view";
import type { Extension } from "@codemirror/state";

/**
 * Styles for the visual mode. Ships inside the visual compartment, so code
 * mode stays pixel-identical. Every color is a CSS var from the design system
 * (same contract as `cmTheme.ts`) — themes keep working without reconfigure.
 */
export const visualTheme: Extension = EditorView.theme({
  // A document, not a terminal: an A4-like sheet centered on a desk, with
  // proportional font and roomier line height. (Line numbers / fold gutter
  // are removed via gutterCompartment — the base theme pins gutters with
  // !important, CSS hiding does not stick.) `&.cm-editor` bumps specificity
  // above cmTheme's rules — CSS injection order between themes is not
  // guaranteed.
  "&.cm-editor": {
    backgroundColor: "color-mix(in srgb, var(--bg-0) 94%, var(--fg-3) 6%)",
  },
  "&.cm-editor .cm-scroller": {
    fontFamily: '"Times New Roman", "PT Serif", Georgia, serif',
    lineHeight: "1.78",
    backgroundColor: "color-mix(in srgb, var(--bg-0) 94%, var(--fg-3) 6%)",
    scrollPadding: "24px 0 72px",
  },
  "&.cm-editor .cm-content": {
    boxSizing: "border-box",
    width: "calc(100% - 48px)",
    maxWidth: "var(--visual-page-width, 816px)",
    minHeight: "calc(100% - 72px)",
    margin: "24px auto 64px",
    padding:
      "var(--visual-page-pad-top, 64px) var(--visual-page-pad-x, 72px) var(--visual-page-pad-bottom, 96px)",
    fontSize: "var(--visual-font-size, 15.5px)",
    color: "var(--fg-0)",
    backgroundColor: "var(--bg-1)",
    border: "1px solid var(--border-strong)",
    borderRadius: "10px",
    boxShadow: "var(--shadow-2)",
    transition: "border-color 140ms var(--ease), box-shadow 140ms var(--ease)",
  },
  "&.cm-focused .cm-content": {
    borderColor: "var(--accent-line)",
    boxShadow:
      "var(--shadow-2), 0 0 0 1px color-mix(in srgb, var(--accent) 8%, transparent)",
  },
  "&.cm-editor .cm-activeLine": {
    backgroundColor: "transparent",
  },
  "&.cm-editor .cm-gutters": {
    backgroundColor: "color-mix(in srgb, var(--bg-0) 94%, var(--fg-3) 6%)",
    borderRight: "none",
  },
  // Diagnostics stay visible inline and in the issues panel. Their tiny
  // gutter marker, however, looks detached from the paper in visual mode.
  "&.cm-editor .cm-lintGutter": {
    display: "none !important",
  },
  "&.cm-editor .cm-lint-marker": {
    display: "none !important",
  },

  // Headings
  ".cm-vis-heading": {
    fontWeight: "700",
    color: "var(--fg-0)",
    letterSpacing: "-0.015em",
  },
  ".cm-vis-h1": { fontSize: "1.65em" },
  ".cm-vis-h2": { fontSize: "1.4em" },
  ".cm-vis-h3": { fontSize: "1.18em" },
  ".cm-vis-h4": { fontSize: "1.06em" },
  ".cm-vis-h5": { fontSize: "1em", fontStyle: "italic" },
  ".cm-vis-heading-line": {
    paddingTop: "0.7em",
    paddingBottom: "0.2em",
  },
  ".cm-vis-align-left": { textAlign: "left" },
  ".cm-vis-align-center": { textAlign: "center" },
  ".cm-vis-align-right": { textAlign: "right" },

  // Protected generated includes (e.g. approval + title pages). This is an
  // honest structural placeholder; exact page pixels belong to the PDF view.
  ".cm-vis-embedded-input": {
    boxSizing: "border-box",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    width: "100%",
    minHeight: "5.375rem",
    margin: "0.5rem 0 1.125rem",
    padding: "0.75rem 0.875rem",
    gap: "0.8125rem",
    border: "1px solid var(--accent-line)",
    borderRadius: "0.625rem",
    background:
      "linear-gradient(135deg, color-mix(in srgb, var(--accent) 9%, var(--bg-1)), var(--bg-1) 58%)",
    boxShadow: "0 8px 24px color-mix(in srgb, #000 9%, transparent)",
    color: "var(--fg-0)",
    fontFamily: "var(--font-sans)",
    lineHeight: "1.25",
    userSelect: "none",
  },
  ".cm-vis-embedded-input-preview": {
    position: "relative",
    display: "inline-block",
    width: "3rem",
    height: "3.625rem",
    flex: "0 0 3rem",
  },
  ".cm-vis-embedded-input-preview > span": {
    position: "absolute",
    boxSizing: "border-box",
    width: "2.25rem",
    height: "3.125rem",
    border: "1px solid var(--border-strong)",
    borderRadius: "0.1875rem",
    background: "var(--bg-0)",
    boxShadow: "var(--shadow-1)",
  },
  ".cm-vis-embedded-input-preview > span:first-child": {
    left: "0",
    top: "0.4375rem",
    opacity: "0.7",
  },
  ".cm-vis-embedded-input-preview > span:last-child": {
    left: "0.625rem",
    top: "0",
    borderColor: "var(--accent-line)",
  },
  ".cm-vis-embedded-input-preview > span:last-child::after": {
    content: '""',
    position: "absolute",
    inset: "0.75rem 0.4375rem auto",
    height: "0.125rem",
    background: "var(--accent-line)",
    boxShadow: "0 7px 0 var(--border), 0 14px 0 var(--border)",
  },
  ".cm-vis-embedded-input-copy": {
    display: "flex",
    minWidth: "0",
    flex: "1 1 11.875rem",
    flexDirection: "column",
    gap: "0.1875rem",
  },
  ".cm-vis-embedded-input-copy strong": {
    fontSize: "0.8125rem",
    fontWeight: "700",
  },
  ".cm-vis-embedded-input-copy > span": {
    color: "var(--fg-2)",
    fontSize: "0.6875rem",
  },
  ".cm-vis-embedded-input-copy code": {
    color: "var(--fg-3)",
    fontFamily: "var(--font-mono)",
    fontSize: "0.59375rem",
  },
  ".cm-vis-embedded-input-status": {
    flexShrink: "0",
    padding: "0.25rem 0.4375rem",
    border: "1px solid var(--border)",
    borderRadius: "999px",
    background: "var(--bg-2)",
    color: "var(--fg-2)",
    fontSize: "0.59375rem",
    whiteSpace: "nowrap",
  },
  ".cm-vis-embedded-input-open": {
    minHeight: "2.125rem",
    flexShrink: "0",
    padding: "0 0.625rem",
    border: "1px solid var(--accent-line)",
    borderRadius: "0.4375rem",
    background: "var(--accent-soft)",
    color: "var(--accent)",
    fontFamily: "var(--font-sans)",
    fontSize: "0.65625rem",
    fontWeight: "600",
    cursor: "pointer",
  },
  ".cm-vis-embedded-input-open:hover": {
    borderColor: "var(--accent)",
    background: "color-mix(in srgb, var(--accent) 17%, transparent)",
  },
  ".cm-vis-embedded-input-open:focus-visible": {
    outline: "2px solid var(--accent)",
    outlineOffset: "2px",
  },

  // Shared shell for semantic, source-backed input previews.
  ".cm-vis-source-block": {
    boxSizing: "border-box",
    width: "100%",
    minWidth: "0",
    margin: "0.625rem 0 1.375rem",
    border: "1px solid var(--accent-line)",
    borderRadius: "0.625rem",
    background: "var(--bg-1)",
    boxShadow: "0 8px 24px color-mix(in srgb, #000 9%, transparent)",
    overflow: "hidden",
    color: "var(--fg-0)",
  },
  ".cm-vis-source-toolbar": {
    boxSizing: "border-box",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "0.625rem",
    padding: "0.6875rem 0.8125rem",
    borderBottom: "1px solid var(--border)",
    background:
      "linear-gradient(135deg, color-mix(in srgb, var(--accent) 9%, var(--bg-1)), var(--bg-1) 62%)",
    fontFamily: "var(--font-sans)",
    lineHeight: "1.25",
    userSelect: "none",
  },
  ".cm-vis-source-copy": {
    display: "flex",
    minWidth: "9.375rem",
    flex: "1 1 13.75rem",
    flexDirection: "column",
    gap: "0.125rem",
  },
  ".cm-vis-source-copy strong": { fontSize: "0.78125rem", fontWeight: "700" },
  ".cm-vis-source-copy > span": {
    color: "var(--fg-2)",
    fontSize: "0.65625rem",
  },
  ".cm-vis-source-copy code": {
    color: "var(--fg-3)",
    fontFamily: "var(--font-mono)",
    fontSize: "0.5625rem",
  },
  ".cm-vis-source-status": {
    flexShrink: "0",
    padding: "0.25rem 0.4375rem",
    border: "1px solid var(--border)",
    borderRadius: "999px",
    background: "var(--bg-2)",
    color: "var(--fg-2)",
    fontFamily: "var(--font-sans)",
    fontSize: "0.59375rem",
    whiteSpace: "nowrap",
  },
  ".cm-vis-source-open": {
    minHeight: "2.125rem",
    flexShrink: "0",
    padding: "0 0.625rem",
    border: "1px solid var(--accent-line)",
    borderRadius: "0.4375rem",
    background: "var(--accent-soft)",
    color: "var(--accent)",
    fontFamily: "var(--font-sans)",
    fontSize: "0.65625rem",
    fontWeight: "600",
    cursor: "pointer",
  },
  ".cm-vis-source-open:hover": {
    borderColor: "var(--accent)",
    background: "color-mix(in srgb, var(--accent) 17%, transparent)",
  },
  ".cm-vis-source-open:focus-visible": {
    outline: "2px solid var(--accent)",
    outlineOffset: "2px",
  },
  ".cm-vis-source-notice": {
    padding: "0.4375rem 0.8125rem",
    borderBottom: "1px solid var(--border)",
    background: "color-mix(in srgb, var(--accent) 4%, var(--bg-1))",
    color: "var(--fg-2)",
    fontFamily: "var(--font-sans)",
    fontSize: "0.59375rem",
    lineHeight: "1.35",
  },
  ".cm-vis-generic-snippets": {
    display: "grid",
    gap: "0.4375rem",
    padding: "0.75rem 0.8125rem 0.875rem",
    background: "var(--bg-1)",
    fontFamily: '"Times New Roman", "PT Serif", Georgia, serif',
  },
  ".cm-vis-generic-snippet": {
    padding: "0.5rem 0.625rem",
    borderLeft: "2px solid var(--accent-line)",
    borderRadius: "0 0.3125rem 0.3125rem 0",
    background: "color-mix(in srgb, var(--fg-3) 4%, transparent)",
    color: "var(--fg-0)",
    fontSize: "0.6875rem",
    lineHeight: "1.4",
  },
  ".cm-vis-generic-empty": {
    padding: "1rem 0.625rem",
    color: "var(--fg-3)",
    fontFamily: "var(--font-sans)",
    fontSize: "0.65625rem",
    fontStyle: "italic",
    textAlign: "center",
  },
  ".cm-vis-title-pages": {
    boxSizing: "border-box",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(16.875rem, 100%), 1fr))",
    alignItems: "start",
    gap: "0.875rem",
    padding: "1rem",
    background: "color-mix(in srgb, var(--fg-3) 7%, var(--bg-2))",
  },
  ".cm-vis-title-block.is-vkr .cm-vis-title-pages": {
    display: "block",
  },
  ".cm-vis-title-page": {
    position: "relative",
    boxSizing: "border-box",
    width: "100%",
    aspectRatio: "210 / 297",
    minHeight: "26.25rem",
    padding: "1.75rem 1.5rem 1.5rem 2.125rem",
    border: "1px solid #d7d9de",
    borderRadius: "0.125rem",
    background: "#fff",
    boxShadow: "0 3px 12px rgba(17, 24, 39, 0.11)",
    overflow: "hidden",
    color: "#17191d",
    fontFamily: '"Times New Roman", "PT Serif", Georgia, serif',
    fontSize: "0.53125rem",
    lineHeight: "1.25",
    whiteSpace: "normal",
  },
  ".cm-vis-title-approval::before, .cm-vis-title-main::before": {
    content:
      '"Подп. и дата · Инв. № дубл. · Взам. инв. № · Подп. и дата · Инв. № подл."',
    position: "absolute",
    inset: "1.375rem auto 1.375rem 0.5rem",
    display: "grid",
    placeItems: "center",
    width: "0.9375rem",
    border: "1px solid #6b7280",
    fontSize: "0.34375rem",
    lineHeight: "1",
    writingMode: "vertical-rl",
    transform: "rotate(180deg)",
  },
  ".cm-vis-title-page-badge": {
    position: "absolute",
    top: "0.4375rem",
    right: "0.5rem",
    color: "#8a8f99",
    fontFamily: "var(--font-sans)",
    fontSize: "0.40625rem",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  },
  ".cm-vis-title-institution": {
    fontWeight: "700",
    lineHeight: "1.35",
    textAlign: "center",
    whiteSpace: "pre-line",
  },
  ".cm-vis-title-signatures": {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
    gap: "1rem",
    marginTop: "1.25rem",
    whiteSpace: "pre-line",
  },
  ".cm-vis-title-signature-heading": {
    marginBottom: "0.3125rem",
    fontWeight: "700",
  },
  ".cm-vis-title-signature-line": {
    height: "0.5rem",
    margin: "0.1875rem 0",
    borderBottom: "1px solid #4b5563",
  },
  ".cm-vis-title-secondary": { marginTop: "0.5rem" },
  ".cm-vis-title-work": {
    marginTop: "2rem",
    textAlign: "center",
  },
  ".cm-vis-title-project": {
    margin: "0.5rem 0",
    fontSize: "0.8125rem",
    fontWeight: "700",
    lineHeight: "1.2",
  },
  ".cm-vis-title-document": { fontSize: "0.6875rem", fontWeight: "700" },
  ".cm-vis-title-main-label": { marginTop: "0.25rem", fontWeight: "700" },
  ".cm-vis-title-code": { marginTop: "0.3125rem", fontWeight: "700" },
  ".cm-vis-title-executor": {
    position: "absolute",
    right: "1.5rem",
    bottom: "1.5rem",
    maxWidth: "48%",
    textAlign: "left",
    whiteSpace: "pre-line",
  },
  ".cm-vis-title-approved": {
    textAlign: "center",
    fontWeight: "700",
    whiteSpace: "pre-line",
  },
  ".cm-vis-title-page-count": { marginTop: "1.5rem", color: "#555b66" },
  ".cm-vis-title-year": {
    position: "absolute",
    right: "1.5rem",
    bottom: "1.25rem",
    left: "2.125rem",
    textAlign: "center",
    fontWeight: "700",
  },
  ".cm-vis-vkr-title-page": {
    maxWidth: "32.5rem",
    minHeight: "43.125rem",
    margin: "0 auto",
    padding: "2.25rem 2.625rem 1.75rem",
    fontSize: "0.625rem",
  },
  ".cm-vis-vkr-meta": {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    alignItems: "start",
    gap: "1.875rem",
    marginTop: "0.9375rem",
  },
  ".cm-vis-vkr-approval": { textAlign: "center", whiteSpace: "pre-line" },
  ".cm-vis-vkr-title-page .cm-vis-title-work": { marginTop: "2.25rem" },
  ".cm-vis-title-topic-label": { marginTop: "0.75rem" },
  ".cm-vis-vkr-people": { marginTop: "2.375rem", gap: "2.125rem" },
  ".cm-vis-vkr-cosupervisor": {
    width: "45%",
    marginTop: "1.5rem",
    whiteSpace: "pre-line",
  },

  // Source-backed longtable shared by TZ and PMI.
  ".cm-vis-requirements-warning": {
    padding: "0.4375rem 0.8125rem",
    borderBottom: "1px solid var(--border)",
    background: "color-mix(in srgb, var(--c-warn) 9%, var(--bg-1))",
    color: "var(--fg-1)",
    fontFamily: "var(--font-sans)",
    fontSize: "0.59375rem",
    lineHeight: "1.35",
  },
  ".cm-vis-requirements-warning::before": {
    content: '"!"',
    display: "inline-grid",
    placeItems: "center",
    width: "0.9375rem",
    height: "0.9375rem",
    marginRight: "0.375rem",
    borderRadius: "50%",
    background: "color-mix(in srgb, var(--c-warn) 18%, transparent)",
    color: "var(--c-warn)",
    fontSize: "0.5625rem",
    fontWeight: "800",
  },
  ".cm-vis-requirements-page": {
    boxSizing: "border-box",
    minWidth: "0",
    padding: "1.125rem 0.9375rem 1rem",
    background: "var(--bg-1)",
    fontFamily: '"Times New Roman", "PT Serif", Georgia, serif',
  },
  ".cm-vis-requirements-caption": {
    margin: "0 0 0.625rem",
    color: "var(--fg-0)",
    fontSize: "0.65625rem",
    lineHeight: "1.3",
    textAlign: "left",
  },
  ".cm-vis-requirements-scroll": {
    boxSizing: "border-box",
    width: "100%",
    maxWidth: "100%",
    overflowX: "auto",
    overscrollBehaviorInline: "contain",
    scrollbarWidth: "thin",
  },
  ".cm-vis-requirements-table": {
    width: "100%",
    minWidth: "38.75rem",
    borderCollapse: "collapse",
    tableLayout: "fixed",
    color: "var(--fg-0)",
    fontSize: "0.5625rem",
    lineHeight: "1.25",
  },
  ".cm-vis-requirements-table th, .cm-vis-requirements-table td": {
    boxSizing: "border-box",
    border: "1px solid var(--fg-2)",
    padding: "0.4375rem 0.375rem",
    textAlign: "left",
    verticalAlign: "top",
    overflowWrap: "anywhere",
  },
  ".cm-vis-requirements-table th": {
    background: "color-mix(in srgb, var(--fg-3) 7%, transparent)",
    fontWeight: "700",
    textAlign: "center",
    verticalAlign: "middle",
  },
  ".cm-vis-requirements-table td:first-child, .cm-vis-requirements-table td:last-child":
    {
      textAlign: "center",
    },
  ".cm-vis-requirements-table td:first-child": {
    color: "var(--accent)",
    fontFamily: "var(--font-mono)",
    fontSize: "0.5rem",
    fontWeight: "650",
  },
  ".cm-vis-requirements-empty": {
    height: "3.625rem",
    color: "var(--fg-3)",
    fontFamily: "var(--font-sans)",
    fontStyle: "italic",
    textAlign: "center !important",
    verticalAlign: "middle !important",
  },

  // Source-backed shared registration sheet. The table is semantic and
  // scrolls inside its own page on narrow screens, never widening CodeMirror.
  ".cm-vis-change-log": {
    boxSizing: "border-box",
    width: "100%",
    minWidth: "0",
    margin: "0.625rem 0 1.375rem",
    border: "1px solid var(--accent-line)",
    borderRadius: "0.625rem",
    background: "var(--bg-1)",
    boxShadow: "0 8px 24px color-mix(in srgb, #000 9%, transparent)",
    overflow: "hidden",
    color: "var(--fg-0)",
  },
  ".cm-vis-change-log-toolbar": {
    boxSizing: "border-box",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "0.625rem",
    padding: "0.6875rem 0.8125rem",
    borderBottom: "1px solid var(--border)",
    background:
      "linear-gradient(135deg, color-mix(in srgb, var(--accent) 9%, var(--bg-1)), var(--bg-1) 62%)",
    fontFamily: "var(--font-sans)",
    lineHeight: "1.25",
    userSelect: "none",
  },
  ".cm-vis-change-log-copy": {
    display: "flex",
    minWidth: "9.375rem",
    flex: "1 1 13.75rem",
    flexDirection: "column",
    gap: "0.125rem",
  },
  ".cm-vis-change-log-copy strong": {
    fontSize: "0.78125rem",
    fontWeight: "700",
  },
  ".cm-vis-change-log-copy > span": {
    color: "var(--fg-2)",
    fontSize: "0.65625rem",
  },
  ".cm-vis-change-log-copy code": {
    color: "var(--fg-3)",
    fontFamily: "var(--font-mono)",
    fontSize: "0.5625rem",
  },
  ".cm-vis-change-log-status": {
    flexShrink: "0",
    padding: "0.25rem 0.4375rem",
    border: "1px solid var(--border)",
    borderRadius: "999px",
    background: "var(--bg-2)",
    color: "var(--fg-2)",
    fontFamily: "var(--font-sans)",
    fontSize: "0.59375rem",
    whiteSpace: "nowrap",
  },
  ".cm-vis-change-log-open": {
    minHeight: "2.125rem",
    flexShrink: "0",
    padding: "0 0.625rem",
    border: "1px solid var(--accent-line)",
    borderRadius: "0.4375rem",
    background: "var(--accent-soft)",
    color: "var(--accent)",
    fontFamily: "var(--font-sans)",
    fontSize: "0.65625rem",
    fontWeight: "600",
    cursor: "pointer",
  },
  ".cm-vis-change-log-open:hover": {
    borderColor: "var(--accent)",
    background: "color-mix(in srgb, var(--accent) 17%, transparent)",
  },
  ".cm-vis-change-log-open:focus-visible": {
    outline: "2px solid var(--accent)",
    outlineOffset: "2px",
  },
  ".cm-vis-change-log-warning": {
    padding: "0.4375rem 0.8125rem",
    borderBottom: "1px solid var(--border)",
    background: "color-mix(in srgb, var(--c-warn) 9%, var(--bg-1))",
    color: "var(--fg-1)",
    fontFamily: "var(--font-sans)",
    fontSize: "0.59375rem",
    lineHeight: "1.35",
  },
  ".cm-vis-change-log-warning::before": {
    content: '"!"',
    display: "inline-grid",
    placeItems: "center",
    width: "0.9375rem",
    height: "0.9375rem",
    marginRight: "0.375rem",
    borderRadius: "50%",
    background: "color-mix(in srgb, var(--c-warn) 18%, transparent)",
    color: "var(--c-warn)",
    fontSize: "0.5625rem",
    fontWeight: "800",
  },
  ".cm-vis-change-log-page": {
    boxSizing: "border-box",
    minWidth: "0",
    padding: "1.125rem 0.9375rem 1rem",
    background: "var(--bg-1)",
    fontFamily: '"Times New Roman", "PT Serif", Georgia, serif',
  },
  ".cm-vis-change-log-page-header": {
    display: "flex",
    minHeight: "1.75rem",
    flexDirection: "column",
    alignItems: "center",
    color: "var(--fg-1)",
    fontSize: "0.53125rem",
    lineHeight: "1.25",
  },
  ".cm-vis-change-log-page-header > span:first-child": {
    fontSize: "0.59375rem",
  },
  ".cm-vis-change-log-page-title": {
    margin: "0.625rem 0 1.125rem",
    textAlign: "center",
    fontSize: "0.84375rem",
    fontWeight: "700",
    letterSpacing: "0.01em",
  },
  ".cm-vis-change-log-table-scroll": {
    boxSizing: "border-box",
    width: "100%",
    maxWidth: "100%",
    overflowX: "auto",
    overscrollBehaviorInline: "contain",
    scrollbarWidth: "thin",
  },
  ".cm-vis-change-log-table": {
    width: "100%",
    minWidth: "38.75rem",
    borderCollapse: "collapse",
    tableLayout: "fixed",
    color: "var(--fg-0)",
    fontSize: "0.46875rem",
    lineHeight: "1.12",
  },
  ".cm-vis-change-log-table th, .cm-vis-change-log-table td": {
    boxSizing: "border-box",
    border: "1px solid var(--fg-2)",
    padding: "0.125rem 0.125rem",
    textAlign: "center",
    verticalAlign: "middle",
    overflowWrap: "anywhere",
  },
  ".cm-vis-change-log-table th": {
    height: "1.875rem",
    background: "color-mix(in srgb, var(--fg-3) 5%, transparent)",
    fontWeight: "400",
  },
  ".cm-vis-change-log-table thead tr:first-child th": {
    height: "1.5rem",
    fontSize: "0.53125rem",
    fontWeight: "600",
  },
  ".cm-vis-change-log-table tbody td": {
    height: "1.25rem",
  },

  // Inline formatting
  ".cm-vis-b": { fontWeight: "700" },
  ".cm-vis-i": { fontStyle: "italic" },
  ".cm-vis-u": { textDecoration: "underline" },
  ".cm-vis-tt": {
    fontFamily: "var(--font-mono)",
    fontSize: "0.92em",
    background: "var(--bg-2)",
    borderRadius: "0.1875rem",
    padding: "0 0.1875rem",
  },
  ".cm-vis-inline-code": {
    padding: "1px 0.25rem",
    border: "1px solid var(--border)",
    borderRadius: "0.25rem",
    background: "var(--bg-2)",
    color: "var(--fg-1)",
    fontFamily: "var(--font-mono)",
    fontSize: "0.88em",
    cursor: "text",
  },
  ".cm-vis-sc": { fontVariant: "small-caps" },
  ".cm-vis-sup": { verticalAlign: "super", fontSize: "0.8em" },
  ".cm-vis-sub": { verticalAlign: "sub", fontSize: "0.8em" },
  ".cm-vis-strike": { textDecoration: "line-through" },
  ".cm-vis-comment": {
    color: "var(--fg-3)",
    fontStyle: "italic",
    opacity: "0.9",
    fontSize: "0.9em",
  },

  // List bullets
  ".cm-vis-bullet": {
    display: "inline-block",
    minWidth: "1.35em",
    color: "var(--fg-1)",
  },
  ".cm-vis-bullet-description": { color: "var(--fg-0)" },

  // Preamble banner
  ".cm-vis-preamble": {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    margin: "0 0 0.375rem",
    padding: "0.3125rem 0.75rem",
    background: "var(--bg-1)",
    borderBottom: "1px solid var(--border)",
    color: "var(--fg-2)",
    fontFamily: "var(--font-sans)",
    fontSize: "0.71875rem",
    cursor: "pointer",
    userSelect: "none",
  },
  ".cm-vis-preamble:hover": { color: "var(--fg-0)" },
  ".cm-vis-preamble-chevron": { color: "var(--fg-3)" },

  // Math widgets
  ".cm-vis-math": { cursor: "pointer" },
  ".cm-vis-math-pending": {
    fontFamily: "var(--font-mono)",
    fontSize: "0.9em",
    color: "var(--fg-2)",
  },
  ".cm-vis-math-error": {
    fontFamily: "var(--font-mono)",
    fontSize: "0.9em",
    color: "var(--c-err)",
  },
  ".cm-vis-math-display": {
    textAlign: "center",
    padding: "0.375rem 0",
  },

  // Reference / citation / label / graphic pills
  ".cm-vis-pill": {
    display: "inline-block",
    padding: "0 0.375rem",
    borderRadius: "999px",
    background: "var(--bg-2)",
    border: "1px solid var(--border)",
    color: "var(--fg-1)",
    fontSize: "0.85em",
    lineHeight: "1.5",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  ".cm-vis-pill:hover": { borderColor: "var(--accent-line)" },
  ".cm-vis-pill-label": { opacity: "0.7" },
  ".cm-vis-pill-link": {
    color: "var(--accent)",
    borderColor: "color-mix(in srgb, var(--accent) 35%, var(--border))",
  },
  ".cm-vis-pill-req": {
    color: "var(--accent)",
    background: "var(--accent-soft)",
    fontFamily: "var(--font-mono)",
    fontSize: "0.78em",
  },
  ".cm-vis-pill-todo": {
    color: "#8a5a00",
    background: "color-mix(in srgb, #f5a623 22%, transparent)",
    borderColor: "color-mix(in srgb, #f5a623 55%, transparent)",
    fontWeight: "600",
  },
  ".cm-vis-pill-todo:hover": {
    borderColor: "#f5a623",
  },
  ".cm-vis-pill-note": {
    verticalAlign: "super",
    fontSize: "0.7em",
    padding: "0 0.25rem",
    color: "var(--accent)",
  },

  // beamer slide / block header (\begin{frame}{Title} → a titled card head).
  ".cm-vis-slide-head": {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.5625rem",
    width: "100%",
    boxSizing: "border-box",
    margin: "0.625rem 0 0.125rem",
    padding: "0.4375rem 0.75rem",
    borderRadius: "0.5625rem 0.5625rem 0 0",
    border: "1px solid var(--border-strong)",
    borderBottom: "2px solid var(--accent-line)",
    background:
      "linear-gradient(135deg, color-mix(in srgb, var(--accent) 12%, var(--bg-1)), var(--bg-1) 62%)",
    fontFamily: "var(--font-sans)",
    userSelect: "none",
  },
  ".cm-vis-slide-icon": {
    flexShrink: "0",
    fontSize: "0.875rem",
    lineHeight: "1",
  },
  ".cm-vis-slide-title": {
    flex: "1 1 auto",
    minWidth: "0",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "var(--fg-0)",
    fontSize: "0.875rem",
    fontWeight: "700",
  },
  ".cm-vis-slide-badge": {
    flexShrink: "0",
    padding: "1px 0.5625rem",
    borderRadius: "999px",
    background: "var(--bg-2)",
    border: "1px solid var(--border)",
    color: "var(--fg-3)",
    fontSize: "0.6875rem",
    fontWeight: "600",
    whiteSpace: "nowrap",
  },

  // `\captionof{…}{text}` shown as a muted caption line.
  ".cm-vis-caption": {
    color: "var(--fg-2)",
    fontStyle: "italic",
    fontSize: "0.92em",
  },

  // `\hrulefill`/`\dotfill` shown as a fill-in blank line.
  ".cm-vis-formblank": {
    display: "inline-block",
    minWidth: "8.75rem",
    borderBottom: "1px solid var(--fg-3)",
    verticalAlign: "baseline",
  },

  // Bibliography (thebibliography) inline list: a titled header + «Управлять»,
  // a numbered badge with a citation-key chip per \bibitem, a «+ Добавить» foot.
  ".cm-vis-bib-head": {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.5rem",
    margin: "0.375rem 0 0.125rem",
    padding: "0.1875rem 0.25rem 0.5rem",
    borderBottom: "2px solid var(--accent-line)",
    fontFamily: "var(--font-sans)",
    userSelect: "none",
  },
  ".cm-vis-bib-head-icon": { fontSize: "0.875rem", lineHeight: "1" },
  ".cm-vis-bib-head-label": {
    color: "var(--fg-0)",
    fontSize: "0.8125rem",
    fontWeight: "700",
    letterSpacing: "0.01em",
  },
  ".cm-vis-bib-manage, .cm-vis-bib-add": {
    padding: "0.125rem 0.5625rem",
    border: "1px solid var(--border)",
    borderRadius: "0.375rem",
    background: "var(--bg-2)",
    color: "var(--fg-2)",
    fontFamily: "var(--font-sans)",
    fontSize: "0.6875rem",
    fontWeight: "600",
    cursor: "pointer",
    transition: "border-color 120ms var(--ease), color 120ms var(--ease)",
  },
  ".cm-vis-bib-manage:hover, .cm-vis-bib-add:hover": {
    borderColor: "var(--accent-line)",
    color: "var(--accent)",
  },
  ".cm-vis-bib-foot": {
    display: "inline-flex",
    margin: "0.25rem 0 0.5rem",
    userSelect: "none",
  },
  ".cm-vis-bib-add": {
    color: "var(--accent)",
    background: "var(--accent-soft)",
  },
  ".cm-vis-bibitem": {
    display: "inline-flex",
    alignItems: "baseline",
    gap: "0.375rem",
    marginRight: "0.375rem",
    verticalAlign: "baseline",
    userSelect: "none",
  },
  ".cm-vis-bibitem-num": {
    color: "var(--accent)",
    fontWeight: "700",
    fontVariantNumeric: "tabular-nums",
  },
  ".cm-vis-bibitem-key": {
    padding: "0 0.375rem",
    borderRadius: "999px",
    background: "var(--bg-2)",
    border: "1px solid var(--border)",
    color: "var(--fg-3)",
    fontFamily: "var(--font-mono)",
    fontSize: "0.78em",
    lineHeight: "1.5",
    cursor: "pointer",
  },
  ".cm-vis-bibitem-key:hover": {
    borderColor: "var(--accent-line)",
    color: "var(--accent)",
  },

  // Auto-generated document list (\hseManualDocItems → the manuals in the set):
  // a read-only card driven by the «Руководства в комплекте» setting.
  ".cm-vis-manuals": {
    display: "inline-flex",
    flexDirection: "column",
    gap: "0.3125rem",
    margin: "0.125rem 0",
    padding: "0.5625rem 0.8125rem",
    border: "1px dashed var(--accent-line)",
    borderRadius: "0.5625rem",
    background: "var(--accent-soft)",
    fontFamily: "var(--font-sans)",
    fontSize: "0.92em",
    lineHeight: "1.4",
    cursor: "pointer",
    userSelect: "none",
    transition: "border-color 120ms var(--ease)",
  },
  ".cm-vis-manuals:hover": { borderColor: "var(--accent)" },
  ".cm-vis-manuals-badge": {
    alignSelf: "flex-start",
    padding: "1px 0.5rem",
    borderRadius: "999px",
    background: "var(--bg-2)",
    border: "1px solid var(--border)",
    color: "var(--fg-3)",
    fontSize: "0.66em",
    fontWeight: "700",
    letterSpacing: "0.05em",
    textTransform: "uppercase",
  },
  ".cm-vis-manuals-list": {
    display: "flex",
    flexDirection: "column",
    gap: "0.1875rem",
  },
  ".cm-vis-manuals-item": {
    display: "flex",
    alignItems: "baseline",
    gap: "0.4375rem",
    color: "var(--fg-1)",
  },
  ".cm-vis-manuals-icon": { flexShrink: "0", fontSize: "0.9em" },

  // Framed raw environments (prose / non-full-line fallback)
  ".cm-vis-envframe": {
    borderLeft: "2px solid var(--accent-line)",
    paddingLeft: "0.625rem",
    background: "color-mix(in srgb, var(--bg-2) 45%, transparent)",
    fontFamily: "var(--font-mono)",
    fontSize: "0.86em",
  },
  ".cm-vis-envname": { color: "var(--accent)", opacity: "0.8" },

  // Inline block cards: a whole table / figure / non-prose environment shown
  // as an editable card. The whole card reveals the raw LaTeX on click, the
  // same escape hatch as formulas. One shared shell, three bodies.
  ".cm-vis-card": {
    boxSizing: "border-box",
    width: "100%",
    minWidth: "0",
    margin: "0.75rem 0 1.25rem",
    border: "1px solid var(--border-strong)",
    borderRadius: "0.625rem",
    background: "var(--bg-1)",
    boxShadow: "0 6px 20px color-mix(in srgb, #000 8%, transparent)",
    overflow: "hidden",
    color: "var(--fg-0)",
    cursor: "pointer",
    transition: "border-color 140ms var(--ease), box-shadow 140ms var(--ease)",
  },
  ".cm-vis-card:hover": {
    borderColor: "var(--accent-line)",
    boxShadow: "0 8px 26px color-mix(in srgb, var(--accent) 12%, transparent)",
  },
  ".cm-vis-card-head": {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    padding: "0.5rem 0.75rem",
    borderBottom: "1px solid var(--border)",
    background:
      "linear-gradient(135deg, color-mix(in srgb, var(--accent) 8%, var(--bg-1)), var(--bg-1) 62%)",
    fontFamily: "var(--font-sans)",
    userSelect: "none",
  },
  ".cm-vis-card-icon": {
    flexShrink: "0",
    fontSize: "0.8125rem",
    lineHeight: "1",
  },
  ".cm-vis-card-label": {
    flex: "1 1 auto",
    minWidth: "0",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "var(--fg-0)",
    fontSize: "0.78125rem",
    fontWeight: "700",
  },
  ".cm-vis-card-hint": {
    flexShrink: "0",
    color: "var(--fg-3)",
    fontSize: "0.625rem",
    whiteSpace: "nowrap",
  },
  ".cm-vis-card-code": {
    flexShrink: "0",
    padding: "0.125rem 0.4375rem",
    border: "1px solid var(--border)",
    borderRadius: "0.375rem",
    background: "var(--bg-2)",
    color: "var(--fg-3)",
    fontFamily: "var(--font-mono)",
    fontSize: "0.625rem",
    cursor: "pointer",
  },
  ".cm-vis-card-code:hover": {
    borderColor: "var(--accent-line)",
    color: "var(--accent)",
  },
  ".cm-vis-card-empty": {
    padding: "1rem 0.875rem",
    color: "var(--fg-3)",
    fontFamily: "var(--font-sans)",
    fontSize: "0.6875rem",
    fontStyle: "italic",
    textAlign: "center",
  },
  ".cm-vis-doc-table-scroll": {
    boxSizing: "border-box",
    width: "100%",
    maxWidth: "100%",
    padding: "0.75rem 0.875rem 0.875rem",
    overflowX: "auto",
    overscrollBehaviorInline: "contain",
    scrollbarWidth: "thin",
    background: "var(--bg-1)",
  },
  ".cm-vis-doc-table-grid": {
    width: "100%",
    borderCollapse: "collapse",
    color: "var(--fg-0)",
    fontFamily: '"Times New Roman", "PT Serif", Georgia, serif',
    fontSize: "0.75rem",
    lineHeight: "1.3",
  },
  ".cm-vis-doc-table-grid th, .cm-vis-doc-table-grid td": {
    boxSizing: "border-box",
    border: "1px solid var(--fg-2)",
    padding: "0.375rem 0.5rem",
    textAlign: "left",
    verticalAlign: "top",
    overflowWrap: "anywhere",
  },
  ".cm-vis-doc-table-grid th": {
    background: "color-mix(in srgb, var(--fg-3) 8%, transparent)",
    fontWeight: "700",
    textAlign: "center",
    verticalAlign: "middle",
  },
  ".cm-vis-figure-body": {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "0.375rem",
    padding: "0.875rem",
    background: "var(--bg-1)",
  },
  ".cm-vis-figure-body img": {
    maxWidth: "100%",
    maxHeight: "20rem",
    objectFit: "contain",
    borderRadius: "0.25rem",
  },
  ".cm-vis-figure-name": {
    maxWidth: "100%",
    overflow: "hidden",
    color: "var(--fg-3)",
    fontFamily: "var(--font-mono)",
    fontSize: "0.625rem",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  ".cm-vis-figure-broken img": { display: "none" },
  ".cm-vis-figure-broken .cm-vis-figure-name": { color: "var(--c-warn)" },
  ".cm-vis-figure-empty": {
    display: "grid",
    placeItems: "center",
    width: "100%",
    minHeight: "7.5rem",
    padding: "0.75rem",
    border: "1px dashed var(--border-strong)",
    borderRadius: "0.375rem",
    background: "color-mix(in srgb, var(--fg-3) 4%, transparent)",
    color: "var(--fg-3)",
    fontFamily: "var(--font-mono)",
    fontSize: "0.6875rem",
    textAlign: "center",
  },
  ".cm-vis-envcard-peek": {
    maxHeight: "5.5rem",
    padding: "0.625rem 0.875rem 0.75rem",
    background: "var(--bg-1)",
    color: "var(--fg-2)",
    fontFamily: "var(--font-mono)",
    fontSize: "0.6875rem",
    lineHeight: "1.45",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
    overflowWrap: "anywhere",
  },

  // Word-style fields: resolved macro values (\hseProjectName → its value)
  ".cm-vis-field": {
    backgroundColor: "color-mix(in srgb, var(--accent) 13%, transparent)",
    boxShadow:
      "inset 0 -1px 0 color-mix(in srgb, var(--accent) 35%, transparent)",
    borderRadius: "0.1875rem",
    padding: "0 0.1875rem",
    cursor: "pointer",
  },
  ".cm-vis-field:hover": {
    backgroundColor: "color-mix(in srgb, var(--accent) 22%, transparent)",
  },

  // «Заполните сами» — mirrors the pack's yellow \hseFill boxes in the PDF
  ".cm-vis-fill": {
    backgroundColor: "color-mix(in srgb, #f2c94c 32%, transparent)",
    color: "var(--fg-1)",
    fontStyle: "italic",
    borderRadius: "0.1875rem",
    padding: "0 0.1875rem",
    cursor: "pointer",
  },
  ".cm-vis-fill:hover": {
    backgroundColor: "color-mix(in srgb, #f2c94c 48%, transparent)",
  },

  // No-prose setup commands (\addcontentsline, \medskip, \tableofcontents…)
  ".cm-vis-setup": {
    fontFamily: "var(--font-mono)",
    fontSize: "0.72em",
    color: "var(--fg-3)",
    backgroundColor: "var(--bg-2)",
    border: "1px solid var(--border)",
    borderRadius: "999px",
    padding: "1px 0.4375rem",
    verticalAlign: "middle",
    opacity: "0.8",
    cursor: "pointer",
  },

  // Collapsed run of full-line comments
  ".cm-vis-commentblock": {
    color: "var(--fg-3)",
    fontSize: "0.6875rem",
    fontStyle: "italic",
    opacity: "0.7",
    cursor: "pointer",
    userSelect: "none",
    padding: "1px 0",
  },

  // «Подсказка:» hint callouts
  ".cm-vis-hint": {
    display: "flex",
    alignItems: "flex-start",
    gap: "0.5rem",
    margin: "0.25rem 0",
    padding: "0.375rem 0.625rem",
    borderLeft: "3px solid color-mix(in srgb, var(--accent) 55%, transparent)",
    background: "color-mix(in srgb, var(--accent) 7%, transparent)",
    borderRadius: "0 0.375rem 0.375rem 0",
    color: "var(--fg-2)",
    fontSize: "0.75rem",
    fontStyle: "italic",
    lineHeight: "1.5",
    cursor: "pointer",
  },
  ".cm-vis-hint-icon": { flexShrink: "0", fontStyle: "normal" },
  ".cm-vis-hint-text": { flex: "1", minWidth: "0" },
  ".cm-vis-hint-close": {
    flexShrink: "0",
    border: "none",
    background: "transparent",
    color: "var(--fg-3)",
    cursor: "pointer",
    fontSize: "0.6875rem",
    padding: "0 0.125rem",
    lineHeight: "1.4",
  },
  ".cm-vis-hint-close:hover": { color: "var(--c-err)" },

  // «Образец» blocks (hseExample): banner + tinted content lines
  ".cm-vis-example-banner": {
    margin: "0.375rem 0 0",
    padding: "0.25rem 0.625rem",
    background: "color-mix(in srgb, #f2c94c 26%, transparent)",
    borderRadius: "0.375rem 0.375rem 0 0",
    color: "var(--fg-1)",
    fontSize: "0.6875rem",
    letterSpacing: "0.03em",
    cursor: "pointer",
    userSelect: "none",
  },
  ".cm-vis-example-line": {
    background: "color-mix(in srgb, #f2c94c 10%, transparent)",
    borderLeft: "3px solid color-mix(in srgb, #f2c94c 45%, transparent)",
    paddingLeft: "0.625rem",
  },

  // Inline image previews (\includegraphics)
  ".cm-vis-img": {
    display: "inline-flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "0.125rem",
    height: "11.75rem",
    maxWidth: "100%",
    padding: "0.375rem",
    border: "1px solid var(--border)",
    borderRadius: "0.375rem",
    background: "var(--bg-1)",
    cursor: "pointer",
    verticalAlign: "middle",
    overflow: "hidden",
  },
  ".cm-vis-img img": {
    flex: "1",
    minHeight: "0",
    maxWidth: "100%",
    objectFit: "contain",
  },
  ".cm-vis-img-name": {
    color: "var(--fg-3)",
    fontSize: "0.625rem",
    fontFamily: "var(--font-mono)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "100%",
  },
  ".cm-vis-img-broken img": { display: "none" },
  ".cm-vis-img-broken .cm-vis-img-name": { color: "var(--c-warn)" },

  // Page divider (\newpage etc.)
  ".cm-vis-divider": {
    display: "inline-flex",
    alignItems: "center",
    width: "100%",
    gap: "0.5rem",
    color: "var(--fg-3)",
    fontSize: "0.625rem",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    cursor: "pointer",
  },
  ".cm-vis-divider::before, .cm-vis-divider::after": {
    content: '""',
    flex: "1",
    borderTop: "1px dashed var(--border)",
  },

  // `\\` glyph
  ".cm-vis-linebreak": {
    color: "var(--fg-3)",
    fontSize: "0.85em",
    cursor: "pointer",
  },
});
